import { Button, Pagination, Select, Space, Table, Tooltip, notification } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { ITEM_KIND_SHORT_LABEL, ORDER_STATUS_OPTIONS, labelOf } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { OrderRow } from 'pages/star-shared/types';
import {
  TABLE_EMPTY,
  formatDateTime,
  notifyBulk,
  personName,
  runBulk,
} from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import {
  Ellipsis,
  NoWrap,
  OrderStatusTag,
  UserCell,
} from 'pages/star-shared/components/status-tags';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import { colors } from 'theme/tokens';
import { useCurrentRoles } from 'hooks/use-current-roles';

const LIMIT = 20;
const ALL = 'ALL';
type OrderAction = 'deliver' | 'reject';

const ACTION_META: Record<OrderAction, { verb: string; title: string; confirm: string }> = {
  deliver: { verb: 'Đã giao', title: 'Xác nhận đã giao', confirm: 'Đã giao' },
  reject: { verb: 'Đã từ chối', title: 'Từ chối & hoàn Sao', confirm: 'Từ chối & hoàn Sao' },
};

const statusOptions = [{ value: ALL, label: 'Tất cả' }, ...ORDER_STATUS_OPTIONS];

const OrdersTab = () => {
  const [status, setStatus] = useState<string>('PENDING');
  const [user, setUser] = useState<UserPickerValue>(undefined);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading } = usePagedList<OrderRow>(
    API.starsOrders,
    { status: status === ALL ? undefined : status, userId: user?.value, page, limit: LIMIT },
    { refreshKey }
  );
  const pendingView = status === 'PENDING';
  const { isAdmin } = useCurrentRoles();

  const refresh = () => {
    setSelectedRowKeys([]);
    setRefreshKey((k) => k + 1);
  };

  const callOrder = (id: number, act: OrderAction, note?: string, code?: string) =>
    AxiosUtils.patch(`${API.starsOrders}/${id}/${act}`, {
      ...(note ? { note } : {}),
      ...(code && act === 'deliver' ? { code } : {}),
    });

  // Giao không mã = giao tay: Admin ghi chú tuỳ chọn; SMOD bắt buộc lý do; Quản lý không được.
  const manualNoteLabel = isAdmin
    ? 'Ghi chú (không bắt buộc)'
    : 'Lý do nếu giao không mã (bắt buộc, ít nhất 5 ký tự)';

  const openSingle = (order: OrderRow, act: OrderAction) => {
    const meta = ACTION_META[act];
    setAction({
      title: meta.title,
      description: (
        <span>
          Đơn #{order.id} · mã nhận{' '}
          <strong style={{ fontFamily: 'monospace', fontSize: 16 }}>{order.code || '—'}</strong> ·{' '}
          {order.itemName} · {personName(order.user)} · <StarAmount value={order.pricePaid} />
          {act === 'reject' && (
            <div style={{ color: colors.textSecondary, marginTop: 4 }}>
              Sao sẽ được hoàn lại và trả kho.
            </div>
          )}
        </span>
      ),
      noteRequired: act === 'reject',
      noteLabel: act === 'reject' ? 'Lý do từ chối' : manualNoteLabel,
      confirmText: meta.confirm,
      danger: act === 'reject',
      codeField:
        act === 'deliver' && order.status === 'PENDING' && order.itemKind === 'GOODS'
          ? {
              label: 'Mã nhận hàng (6 ký tự người nhận đọc)',
              help: 'Có mã = người nhận có mặt. Bỏ trống là giao tay: bị gắn cờ trong Lịch sử đối chiếu.',
            }
          : undefined,
      onSubmit: async (note, code) => {
        try {
          await callOrder(order.id, act, note, code);
          notification.success({ message: `${meta.verb} đơn #${order.id}.` });
          refresh();
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const openBulk = (act: OrderAction) => {
    const ids = selectedRowKeys.map(Number);
    if (!ids.length) return;
    const meta = ACTION_META[act];
    setAction({
      title: `${meta.title} — ${ids.length} đơn đã chọn`,
      description:
        act === 'deliver'
          ? 'Giao hàng loạt là giao không mã (giao tay) — mỗi đơn bị gắn cờ trong Lịch sử đối chiếu.'
          : 'Ghi chú (nếu có) sẽ áp dụng cho tất cả đơn đã chọn.',
      noteRequired: act === 'reject' || !isAdmin,
      noteLabel: act === 'reject' ? 'Lý do từ chối' : manualNoteLabel,
      confirmText: `${meta.confirm} (${ids.length})`,
      danger: act === 'reject',
      onSubmit: async (note) => {
        const result = await runBulk(ids, (id) => callOrder(id, act, note));
        notifyBulk(result, meta.verb);
        refresh();
      },
    });
  };

  const columns: ColumnsType<OrderRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Mã nhận',
      key: 'code',
      width: 130,
      render: (_, o) =>
        o.code ? (
          <span
            style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, letterSpacing: 2 }}
          >
            {o.code}
          </span>
        ) : (
          '—'
        ),
    },
    { title: 'Người đổi', key: 'user', width: 220, render: (_, o) => <UserCell user={o.user} /> },
    {
      title: 'Món',
      key: 'item',
      width: 220,
      render: (_, o) => (
        <div>
          <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{o.itemName}</div>
          <span style={{ color: colors.textSecondary, fontSize: 12 }}>
            {labelOf(ITEM_KIND_SHORT_LABEL, o.itemKind)}
          </span>
        </div>
      ),
    },
    {
      title: 'Giá',
      key: 'pricePaid',
      width: 100,
      align: 'right',
      render: (_, o) => <StarAmount value={o.pricePaid} />,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, o) => <OrderStatusTag value={o.status} />,
    },
    {
      title: 'Đổi lúc',
      key: 'createdAt',
      width: 160,
      render: (_, o) => <NoWrap>{formatDateTime(o.createdAt)}</NoWrap>,
    },
    {
      title: 'Hết hạn',
      key: 'expiresAt',
      width: 160,
      render: (_, o) => <NoWrap>{formatDateTime(o.expiresAt)}</NoWrap>,
    },
    {
      title: 'Xử lý',
      key: 'handled',
      width: 220,
      render: (_, o) =>
        o.handledAt ? (
          <div>
            <div>
              <NoWrap>{formatDateTime(o.handledAt)}</NoWrap>
              {o.handledBy ? ` · ${personName(o.handledBy)}` : ''}
            </div>
            <Ellipsis value={o.handleNote} width={200} />
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_, o) =>
        o.status === 'PENDING' ? (
          <Space size="small">
            <Tooltip title="Đã giao">
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => openSingle(o, 'deliver')}
              />
            </Tooltip>
            <Tooltip title="Từ chối & hoàn Sao">
              <Button
                danger
                type="text"
                icon={<CloseOutlined />}
                onClick={() => openSingle(o, 'reject')}
              />
            </Tooltip>
          </Space>
        ) : null,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Select
          style={{ width: 170 }}
          value={status}
          options={statusOptions}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
            setSelectedRowKeys([]);
          }}
        />
        <div style={{ width: 280 }}>
          <UserPicker
            value={user}
            onChange={(value) => {
              setUser(value);
              setPage(1);
              setSelectedRowKeys([]);
            }}
            placeholder="Lọc theo người đổi"
          />
        </div>
        {pendingView && (
          <>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!selectedRowKeys.length}
              onClick={() => openBulk('deliver')}
            >
              Đã giao đã chọn
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              disabled={!selectedRowKeys.length}
              onClick={() => openBulk('reject')}
            >
              Từ chối đã chọn
            </Button>
            {selectedRowKeys.length > 0 && <span>Đã chọn {selectedRowKeys.length}</span>}
          </>
        )}
      </div>
      <div className="custom-table">
        <Table
          rowSelection={pendingView ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={pendingView ? { emptyText: 'Không có đơn chờ giao.' } : TABLE_EMPTY}
          scroll={{ x: 1520 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={page}
            onChange={setPage}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default OrdersTab;
